import React, {useEffect, useRef, useState} from 'react';
import {FiCheck, FiTrash2, FiX} from 'react-icons/fi';
import { ImUndo } from "react-icons/im";
import './DrawingCanvas.css';

function DrawingCanvas({isOpen, onClose, onSave, customerId}) {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [context, setContext] = useState(null);
    const [brushSize, setBrushSize] = useState(5);
    const [brushColor, setBrushColor] = useState('#000000');
    const [isSaving, setIsSaving] = useState(false);
    const [canvasImageData, setCanvasImageData] = useState(null);
    const [history, setHistory] = useState([]);
    const [undoStack, setUndoStack] = useState([]);

    // Initialize canvas only when modal opens
    useEffect(() => {
        if (!isOpen) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = brushColor;
        ctx.lineWidth = brushSize;

        // Set white background only on initial load
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        setContext(ctx);
        setCanvasImageData(null); // Reset stored image data when modal opens
    }, [isOpen]);

    // Update context when brush properties change (without clearing canvas)
    useEffect(() => {
        if (!context) return;

        context.strokeStyle = brushColor;
        context.lineWidth = brushSize;
    }, [context, brushSize, brushColor]);

    const pushToHistory = () => {
        if (!canvasRef.current) return;
        const dataUrl = canvasRef.current.toDataURL();
        setHistory(prev => [...prev, dataUrl]);
    };

    const handleUndo = () => {
        if (!undoStack.length || !context) return;

        const last = undoStack[undoStack.length - 1];
        const img = new Image();
        img.onload = () => {
            context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            context.drawImage(img, 0, 0);
            setUndoStack((prev) => prev.slice(0, -1));
        };
        img.src = last;
    };

    const startDrawing = (e) => {
        if (!context) return;

        // Save canvas state before drawing
        if (canvasRef.current) {
            const snapshot = canvasRef.current.toDataURL();
            setUndoStack((prev) => [...prev.slice(-19), snapshot]); // keep last 20 states
        }

        const { offsetX, offsetY } = getCoordinates(e);
        context.beginPath();
        context.moveTo(offsetX, offsetY);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing || !context) return;

        const {offsetX, offsetY} = getCoordinates(e);
        context.lineTo(offsetX, offsetY);
        context.stroke();
    };

    const stopDrawing = () => {
        if (!context) return;

        context.closePath();
        setIsDrawing(false);
        pushToHistory();
    };

    // Get coordinates for touch and mouse events
    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        if (e.type.startsWith('touch')) {
            const touch = e.touches[0] || e.changedTouches[0];
            return {
                offsetX: touch.clientX - rect.left,
                offsetY: touch.clientY - rect.top
            };
        } else {
            return {
                offsetX: e.clientX - rect.left,
                offsetY: e.clientY - rect.top
            };
        }
    };

    const clearCanvas = () => {
        if (!context || !canvasRef.current) return;

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        setCanvasImageData(null); // Clear stored image data
    };

    const saveAsImage = async () => {
        if (!canvasRef.current) return;

        try {
            setIsSaving(true);
            const canvas = canvasRef.current;
            const imageDataURL = canvas.toDataURL('image/png');

            // Create a new image object to return to parent component
            const newImage = {
                id: Date.now() + Math.random(),
                url: imageDataURL,
                isDrawing: true,
                attachType: 'PROJECT'
            };

            if (onSave) {
                onSave(newImage);
            }

            onClose();
        } catch (error) {
            console.error('Error saving drawing:', error);
            alert('A apărut o eroare la salvarea desenului. Vă rugăm să încercați din nou.');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle canvas resize separately and preserve content
    useEffect(() => {
        const resizeCanvas = () => {
            if (!canvasRef.current || !isOpen) return;

            const container = canvasRef.current.parentElement;
            if (!container) return;

            const canvas = canvasRef.current;

            // Save current canvas content before resizing
            const currentImageData = canvas.toDataURL();

            const newWidth = container.clientWidth - 40;
            const newHeight = Math.min(window.innerHeight * 0.6, 500);

            // Only resize if dimensions actually changed
            if (canvas.width !== newWidth || canvas.height !== newHeight) {
                canvas.width = newWidth;
                canvas.height = newHeight;

                const ctx = canvas.getContext('2d');

                // Set white background
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Restore canvas content if it exists
                if (currentImageData && currentImageData !== 'data:,') {
                    const img = new Image();
                    img.onload = () => {
                        ctx.drawImage(img, 0, 0);
                        // Restore context settings after drawing image
                        ctx.lineCap = 'round';
                        ctx.lineJoin = 'round';
                        ctx.strokeStyle = brushColor;
                        ctx.lineWidth = brushSize;
                        setContext(ctx);
                    };
                    img.src = currentImageData;
                } else {
                    // Restore context settings
                    ctx.lineCap = 'round';
                    ctx.lineJoin = 'round';
                    ctx.strokeStyle = brushColor;
                    ctx.lineWidth = brushSize;
                    setContext(ctx);
                }
            }
        };

        if (isOpen) {
            // Initial resize
            setTimeout(resizeCanvas, 100); // Small delay to ensure container is rendered
            window.addEventListener('resize', resizeCanvas);
        }

        return () => {
            window.removeEventListener('resize', resizeCanvas);
        };
    }, [isOpen]); // Remove brushColor and brushSize from dependencies

    if (!isOpen) return null;

    return (
        <div className="drawing-modal-overlay">
            <div className="drawing-modal-content">
                <div className="drawing-modal-header">
                    <h2>Notițe și schițe</h2>
                    <button className="drawing-close-button" onClick={onClose}>
                        <FiX/>
                    </button>
                </div>

                <div className="drawing-controls">
                    <div className="brush-controls">
                        <div className="brush-size-control">
                            <label>Dimensiune:</label>
                            <input
                                type="range"
                                min="1"
                                max="20"
                                value={brushSize}
                                onChange={(e) => setBrushSize(parseInt(e.target.value))}
                            />
                            <span>{brushSize}px</span>
                        </div>
                        <div className="brush-color-control">
                            <input
                                type="color"
                                value={brushColor}
                                onChange={(e) => setBrushColor(e.target.value)}
                            />
                        </div>
                        <div className="brush-undo-control">
                            <button className="undo-button" onClick={handleUndo} title="Undo">
                                <ImUndo />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="canvas-container">
                    <canvas
                        ref={canvasRef}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseOut={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                    />
                </div>

                <div className="drawing-modal-footer">
                    <button className="clear-button" onClick={clearCanvas}>
                        <FiTrash2/>
                        <span>Șterge tot</span>
                    </button>

                    <button
                        className="save-drawing-button"
                        onClick={saveAsImage}
                        disabled={isSaving}
                    >
                        <FiCheck/>
                        <span>{isSaving ? 'Se salvează...' : 'Salvează'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DrawingCanvas;